import React, { useMemo } from 'react';
import Icon from '../common/Icons';
import { AFFILIATION_FILTER_ALL } from '../../utils/affiliationHelpers';
import {
  CITY_FILTER_ALL,
  COMPANY_STATUS_FILTERS
} from './companyListUtils';
import './CompanyListUx.css';

export default function CompanyListFilters({
  search,
  onSearchChange,
  statusFilter,
  onStatusFilterChange,
  affiliationFilter,
  onAffiliationFilterChange,
  affiliationOptions = [],
  cityFilter,
  onCityFilterChange,
  cities = [],
  hasActiveFilters = false,
  onClearFilters,
  onRefresh,
  loading = false,
  filteredCount = 0,
  repositoryTotal = 0,
  statusLabels = COMPANY_STATUS_FILTERS,
  searchPlaceholder = 'Code, nom, ville, email, téléphone…',
  affiliationAllLabel = 'Toutes affiliations',
  cityAllLabel = 'Toutes les villes'
}) {
  const affiliationSelectOptions = useMemo(
    () => [
      { id: AFFILIATION_FILTER_ALL, label: affiliationAllLabel },
      ...affiliationOptions.map((opt) => ({ id: String(opt.id), label: opt.label }))
    ],
    [affiliationOptions, affiliationAllLabel]
  );

  const citySelectOptions = useMemo(
    () => [
      { id: CITY_FILTER_ALL, label: cityAllLabel },
      ...cities.map((c) => ({ id: String(c.id), label: c.label || c.name }))
    ],
    [cities, cityAllLabel]
  );

  return (
    <div className="co-list-filters">
      <div className="co-list-filters__row co-list-filters__row--primary">
        <label className="co-list-search">
          <Icon name="search" size={16} className="co-list-search__icon" aria-hidden />
          <input
            className="co-list-search__input"
            type="search"
            placeholder={searchPlaceholder}
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
            aria-label="Rechercher une société"
            autoComplete="off"
          />
        </label>

        <div
          className="co-list-segment"
          role="group"
          aria-label="Filtrer par statut"
        >
          {statusLabels.map((filter) => (
            <button
              key={filter.id}
              type="button"
              aria-pressed={statusFilter === filter.id}
              className={`co-list-segment__btn ${statusFilter === filter.id ? 'is-active' : ''}`}
              onClick={() => onStatusFilterChange(filter.id)}
            >
              {filter.label}
            </button>
          ))}
        </div>
      </div>

      <div className="co-list-filters__row co-list-filters__row--secondary">
        <label className="co-list-field">
          <span className="co-list-field__label">Affiliation</span>
          <select
            className="co-list-field__select"
            value={affiliationFilter}
            onChange={(e) => onAffiliationFilterChange(e.target.value)}
            aria-label="Filtrer par affiliation SMIE"
          >
            {affiliationSelectOptions.map((opt) => (
              <option key={opt.id} value={opt.id}>
                {opt.label}
              </option>
            ))}
          </select>
        </label>

        <label className="co-list-field">
          <span className="co-list-field__label">Ville</span>
          <select
            className="co-list-field__select"
            value={cityFilter}
            onChange={(e) => onCityFilterChange(e.target.value)}
            aria-label="Filtrer par ville"
          >
            {citySelectOptions.map((opt) => (
              <option key={opt.id} value={opt.id}>
                {opt.label}
              </option>
            ))}
          </select>
        </label>

        {hasActiveFilters ? (
          <button
            type="button"
            className="co-list-clear-btn"
            onClick={onClearFilters}
            aria-label="Réinitialiser tous les filtres"
          >
            <Icon name="times" size={14} aria-hidden />
            <span>Réinitialiser</span>
          </button>
        ) : null}

        <button
          type="button"
          className="co-list-refresh-btn"
          onClick={onRefresh}
          disabled={loading}
          title="Actualiser le répertoire"
          aria-label="Actualiser la liste des sociétés"
        >
          <Icon name="refresh" size={16} />
        </button>

        <div className="co-list-results-meta" role="status" aria-live="polite">
          <span className="co-list-results-meta__count">
            <strong>{filteredCount}</strong>
            <span>
              résultat{filteredCount > 1 ? 's' : ''}
            </span>
          </span>
          <span className="co-list-results-meta__total">répertoire : {repositoryTotal}</span>
        </div>
      </div>
    </div>
  );
}
